import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  Badge,
  Statistic,
  Divider,
  Empty,
  Spin,
  Switch,
  InputNumber,
  Grid,
} from "antd"
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  RobotOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  TagOutlined,
  StarOutlined,
  FireOutlined,
  FilterOutlined,
  ClearOutlined,
  BulbOutlined,
  MessageOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"

// Redux actions
import {
  getAiTraining,
  getAiTrainingCategories,
  upsertAiTraining,
  deleteAiTraining,
  resetAiTrainingFlag,
} from "../../store/actions"

import "./aiTraining.scss"

const { Option } = Select
const { TextArea } = Input
const { Title, Text, Paragraph } = Typography
const { useBreakpoint } = Grid

const AiTraining = ({
  t,
  lstAiTraining,
  categories,
  error,
  loading,
  success,
  onGetAiTraining,
  onGetAiTrainingCategories,
  onUpsertAiTraining,
  onDeleteAiTraining,
  onResetAiTrainingFlag,
}) => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [searchTerm, setSearchTerm] = useState("")
  const [filteredData, setFilteredData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewItem, setViewItem] = useState(null)
  const [filterCategory, setFilterCategory] = useState(null)
  const [filterActive, setFilterActive] = useState(null)
  const [newCategory, setNewCategory] = useState("")

  useEffect(() => {
    onGetAiTraining()
    onGetAiTrainingCategories()
  }, [])

  useEffect(() => {
    setFilteredData(lstAiTraining || [])
    setCurrentPage(1)
  }, [lstAiTraining])

  useEffect(() => {
    let filtered = lstAiTraining || []

    // Filter by search term
    if (searchTerm) {
      const searchText = searchTerm.toLowerCase()
      filtered = filtered.filter(row => {
        const valuesToSearch = [
          row.question,
          row.answer,
          row.category,
          row.keywords,
        ]
        return valuesToSearch.some(value =>
          String(value || "").toLowerCase().includes(searchText)
        )
      })
    }

    // Filter by category
    if (filterCategory) {
      filtered = filtered.filter(row => row.category === filterCategory)
    }

    // Filter by active status
    if (filterActive !== null) {
      filtered = filtered.filter(row => row.isActive === filterActive)
    }

    setFilteredData(filtered)
    setCurrentPage(1)
  }, [searchTerm, lstAiTraining, filterCategory, filterActive])

  useEffect(() => {
    if (success || error) {
      setTimeout(() => onResetAiTrainingFlag(), 3000)
    }
  }, [success, error])

  // Handlers
  const handleAdd = () => {
    setEditMode(false)
    setSelectedItem(null)
    form.resetFields()
    form.setFieldsValue({
      priority: 0,
      isActive: true,
    })
    setNewCategory("")
    setModalVisible(true)
  }

  const handleEdit = record => {
    setEditMode(true)
    setSelectedItem(record)
    form.setFieldsValue({
      question: record.question,
      answer: record.answer,
      category: record.category,
      keywords: record.keywords,
      priority: record.priority,
      isActive: record.isActive,
    })
    setNewCategory("")
    setModalVisible(true)
  }

  const handleView = record => {
    setViewItem(record)
    setViewModalVisible(true)
  }

  const handleDelete = id => {
    onDeleteAiTraining(id)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const finalCategory = newCategory || values.category
      
      const data = {
        ...values,
        category: finalCategory,
      }

      if (editMode && selectedItem) {
        data.id = selectedItem.id
      }

      onUpsertAiTraining(data)
      setModalVisible(false)
      form.resetFields()
    } catch (error) {
      console.error("Validation failed:", error)
    }
  }

  const handleRefresh = () => {
    onGetAiTraining()
    onGetAiTrainingCategories()
  }

  const clearFilters = () => {
    setSearchTerm("")
    setFilterCategory(null)
    setFilterActive(null)
  }

  // Stats
  const totalItems = lstAiTraining?.length || 0
  const activeItems = lstAiTraining?.filter(item => item.isActive)?.length || 0
  const inactiveItems = totalItems - activeItems
  const totalUsage = lstAiTraining?.reduce((sum, item) => sum + (item.usageCount || 0), 0) || 0

  // Table columns
  const columns = [
    {
      title: "سوال",
      dataIndex: "question",
      key: "question",
      ellipsis: true,
      width: 300,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong ellipsis={{ tooltip: text }} style={{ maxWidth: 280 }}>
            {text}
          </Text>
          {record.keywords && (
            <Space size={[0, 4]} wrap style={{ marginTop: 4 }}>
              {record.keywords.split(',').slice(0, 3).map((keyword, idx) => (
                <Tag key={idx} color="default" style={{ fontSize: 10, margin: 0 }}>
                  {keyword.trim()}
                </Tag>
              ))}
              {record.keywords.split(',').length > 3 && (
                <Tag color="default" style={{ fontSize: 10, margin: 0 }}>
                  +{record.keywords.split(',').length - 3}
                </Tag>
              )}
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: "دسته‌بندی",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: text => (
        text ? (
          <Tag icon={<TagOutlined />} color="blue">
            {text}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: "اولویت",
      dataIndex: "priority",
      key: "priority",
      width: 90,
      align: "center",
      sorter: (a, b) => a.priority - b.priority,
      render: priority => (
        <Tag color={priority >= 10 ? "gold" : priority >= 5 ? "blue" : "default"}>
          <StarOutlined style={{ marginLeft: 4 }} />
          {priority}
        </Tag>
      ),
    },
    {
      title: "تعداد استفاده",
      dataIndex: "usageCount",
      key: "usageCount",
      width: 110,
      align: "center",
      sorter: (a, b) => a.usageCount - b.usageCount,
      render: count => (
        <Badge
          count={count || 0}
          showZero
          style={{
            backgroundColor: count >= 50 ? "#f5222d" : count >= 20 ? "#fa8c16" : "#52c41a",
          }}
        />
      ),
    },
    {
      title: "وضعیت",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      align: "center",
      render: isActive => (
        isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            فعال
          </Tag>
        ) : (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            غیرفعال
          </Tag>
        )
      ),
    },
    {
      title: "عملیات",
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: "#1890ff" }} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="آیا از حذف این مورد اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
            placement="topRight"
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // Mobile Card Component
  const MobileCard = ({ record }) => (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        borderRight: record.isActive ? "4px solid #52c41a" : "4px solid #ff4d4f",
      }}
      bodyStyle={{ padding: 12 }}
    >
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {record.category ? (
              <Tag icon={<TagOutlined />} color="blue">{record.category}</Tag>
            ) : (
              <span />
            )}
            {record.isActive ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>فعال</Tag>
            ) : (
              <Tag color="error" icon={<CloseCircleOutlined />}>غیرفعال</Tag>
            )}
          </Space>
        </Col>
        
        <Col span={24}>
          <Space size={4}>
            <QuestionCircleOutlined style={{ color: "#1890ff" }} />
            <Text strong style={{ fontSize: 13 }}>
              {record.question?.length > 60 
                ? record.question.substring(0, 60) + "..." 
                : record.question}
            </Text>
          </Space>
        </Col>

        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.answer?.length > 80 
              ? record.answer.substring(0, 80) + "..." 
              : record.answer}
          </Text>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "8px 0" }} />
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space>
              <Tag color="gold">
                <StarOutlined /> {record.priority}
              </Tag>
              <Badge
                count={record.usageCount || 0}
                showZero
                style={{ backgroundColor: "#52c41a" }}
              />
            </Space>
            <Space>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleView(record)}
              />
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ color: "#1890ff" }} />}
                onClick={() => handleEdit(record)}
              />
              <Popconfirm
                title="آیا از حذف این مورد اطمینان دارید؟"
                onConfirm={() => handleDelete(record.id)}
                okText="بله"
                cancelText="خیر"
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          </Space>
        </Col>
      </Row>
    </Card>
  )

  return (
    <div className="page-content">
      <MetaTags>
        <title>داده‌های آموزشی هوش مصنوعی | واحد گاز</title>
      </MetaTags>

      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
        gutter={[16, 16]}
      >
        <Col>
          <Breadcrumb
            items={[
              { title: "داشبورد" },
              { title: "داده‌های آموزشی AI" },
            ]}
          />
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={handleRefresh}
            >
              بروزرسانی
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              افزودن داده جدید
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}
      {success && (
        <Alert
          message={success}
          type="success"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
            }}
          >
            <Statistic
              title={<Text type="secondary">کل داده‌ها</Text>}
              value={totalItems}
              prefix={<RobotOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)",
            }}
          >
            <Statistic
              title={<Text type="secondary">فعال</Text>}
              value={activeItems}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)",
            }}
          >
            <Statistic
              title={<Text type="secondary">غیرفعال</Text>}
              value={inactiveItems}
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)",
            }}
          >
            <Statistic
              title={<Text type="secondary">کل استفاده</Text>}
              value={totalUsage}
              prefix={<FireOutlined style={{ color: "#fa8c16" }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card
        title={
          <Space>
            <BulbOutlined style={{ color: "#faad14" }} />
            <span>داده‌های آموزشی هوش مصنوعی</span>
          </Space>
        }
        extra={
          <Text type="secondary">
            {filteredData.length} از {totalItems} مورد
          </Text>
        }
        style={{ borderRadius: 12 }}
      >
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="جستجو در سوالات، پاسخ‌ها و کلمات کلیدی..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="دسته‌بندی"
              value={filterCategory}
              onChange={setFilterCategory}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              {categories?.map(cat => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="وضعیت"
              value={filterActive}
              onChange={setFilterActive}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Option value={true}>فعال</Option>
              <Option value={false}>غیرفعال</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              block
            >
              پاک کردن فیلترها
            </Button>
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0" }} />

        {/* Data Display */}
        <Spin spinning={loading}>
          {isMobile ? (
            // Mobile View
            <div>
              {filteredData.length > 0 ? (
                filteredData
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map(record => (
                    <MobileCard key={record.id} record={record} />
                  ))
              ) : (
                <Empty description="داده‌ای یافت نشد" />
              )}
            </div>
          ) : (
            // Desktop Table View
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} از ${total} مورد`,
                onChange: (page, size) => {
                  setCurrentPage(page)
                  setPageSize(size)
                },
              }}
              scroll={{ x: 900 }}
              size="middle"
              locale={{
                emptyText: <Empty description="داده‌ای یافت نشد" />,
              }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space>
            {editMode ? <EditOutlined /> : <PlusOutlined />}
            <span>{editMode ? "ویرایش داده آموزشی" : "افزودن داده آموزشی جدید"}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            انصراف
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            {editMode ? "به‌روزرسانی" : "ذخیره"}
          </Button>,
        ]}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="question"
            label={
              <Space>
                <QuestionCircleOutlined />
                <span>سوال / الگوی تشخیص</span>
              </Space>
            }
            rules={[{ required: true, message: "سوال الزامی است" }]}
          >
            <TextArea
              rows={3}
              placeholder="مثال: اگر در مورد ارجاع به مهندس سوال پرسیده شود"
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="answer"
            label={
              <Space>
                <MessageOutlined />
                <span>پاسخ</span>
              </Space>
            }
            rules={[{ required: true, message: "پاسخ الزامی است" }]}
          >
            <TextArea
              rows={5}
              placeholder="پاسخی که هوش مصنوعی باید ارائه دهد..."
              maxLength={4000}
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="category"
                label={
                  <Space>
                    <TagOutlined />
                    <span>دسته‌بندی</span>
                  </Space>
                }
              >
                <Select
                  placeholder="انتخاب یا ایجاد دسته‌بندی"
                  allowClear
                  dropdownRender={menu => (
                    <>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <Space style={{ padding: "0 8px 4px" }}>
                        <Input
                          placeholder="دسته‌بندی جدید"
                          value={newCategory}
                          onChange={e => setNewCategory(e.target.value)}
                          onKeyDown={e => e.stopPropagation()}
                        />
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            if (newCategory) {
                              form.setFieldsValue({ category: newCategory })
                            }
                          }}
                        >
                          افزودن
                        </Button>
                      </Space>
                    </>
                  )}
                >
                  {categories?.map(cat => (
                    <Option key={cat} value={cat}>
                      {cat}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="keywords"
                label={
                  <Space>
                    <TagOutlined />
                    <span>کلمات کلیدی</span>
                  </Space>
                }
                extra="کلمات کلیدی را با کاما جدا کنید"
              >
                <Input placeholder="مثال: ارجاع,مهندس,پرونده" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item
                name="priority"
                label={
                  <Space>
                    <StarOutlined />
                    <span>اولویت</span>
                  </Space>
                }
                extra="عدد بالاتر = اولویت بیشتر"
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item
                name="isActive"
                label="وضعیت"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>مشاهده جزئیات</span>
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            بستن
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setViewModalVisible(false)
              handleEdit(viewItem)
            }}
          >
            ویرایش
          </Button>,
        ]}
        width={600}
      >
        {viewItem && (
          <div style={{ padding: "16px 0" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small" style={{ background: "#f5f5f5" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="secondary">
                      <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                      سوال:
                    </Text>
                    <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {viewItem.question}
                    </Paragraph>
                  </Space>
                </Card>
              </Col>

              <Col span={24}>
                <Card size="small" style={{ background: "#e6f7ff" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="secondary">
                      <MessageOutlined style={{ marginLeft: 8 }} />
                      پاسخ:
                    </Text>
                    <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {viewItem.answer}
                    </Paragraph>
                  </Space>
                </Card>
              </Col>

              <Col xs={12}>
                <Text type="secondary">دسته‌بندی:</Text>
                <br />
                {viewItem.category ? (
                  <Tag icon={<TagOutlined />} color="blue">
                    {viewItem.category}
                  </Tag>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Col>

              <Col xs={12}>
                <Text type="secondary">وضعیت:</Text>
                <br />
                {viewItem.isActive ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    فعال
                  </Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    غیرفعال
                  </Tag>
                )}
              </Col>

              <Col xs={12}>
                <Text type="secondary">اولویت:</Text>
                <br />
                <Tag color="gold">
                  <StarOutlined /> {viewItem.priority}
                </Tag>
              </Col>

              <Col xs={12}>
                <Text type="secondary">تعداد استفاده:</Text>
                <br />
                <Badge
                  count={viewItem.usageCount || 0}
                  showZero
                  style={{ backgroundColor: "#52c41a" }}
                />
              </Col>

              {viewItem.keywords && (
                <Col span={24}>
                  <Text type="secondary">کلمات کلیدی:</Text>
                  <br />
                  <Space size={[4, 4]} wrap style={{ marginTop: 8 }}>
                    {viewItem.keywords.split(',').map((keyword, idx) => (
                      <Tag key={idx} color="default">
                        {keyword.trim()}
                      </Tag>
                    ))}
                  </Space>
                </Col>
              )}

              <Divider style={{ margin: "12px 0" }} />

              <Col xs={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ایجاد شده توسط: {viewItem.createdBy || "-"}
                </Text>
              </Col>
              <Col xs={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  آخرین ویرایش: {viewItem.modifiedBy || "-"}
                </Text>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  )
}

const mapStateToProps = ({ AiTraining }) => ({
  lstAiTraining: AiTraining.lstAiTraining,
  categories: AiTraining.categories,
  error: AiTraining.error,
  success: AiTraining.success,
  loading: AiTraining.loading,
})

const mapDispatchToProps = dispatch => ({
  onGetAiTraining: (activeOnly, category) => dispatch(getAiTraining(activeOnly, category)),
  onGetAiTrainingCategories: () => dispatch(getAiTrainingCategories()),
  onUpsertAiTraining: data => dispatch(upsertAiTraining(data)),
  onDeleteAiTraining: id => dispatch(deleteAiTraining(id)),
  onResetAiTrainingFlag: () => dispatch(resetAiTrainingFlag()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AiTraining))
